/**
 * Everything the Fan panel shows about one fan on one Account: OnlyFans
 * profile (identity, since, spend, OnlyFans note, lists), PPV ledger, buying
 * power, the team's Fan note. Field fallbacks follow the CRM's fan-panel.js.
 */
import { useCallback, useEffect, useState } from 'react';
import { crmFetch, dataOf, listOf } from './api';

type Raw = Record<string, unknown>;

export interface FanList {
  id: string;
  name: string;
}

export interface FanProfile {
  name: string;
  username: string;
  avatar: string | null;
  /** OnlyFans' verified badge. */
  verified: boolean;
  isOnline: boolean;
  lastSeen: string | null;
  subscribedAt: string | null;
  duration: string | null;
  spent: { total: number | null; tips: number | null; ppv: number | null; subs: number | null };
  ofNote: string;
}

export interface FanData {
  profile: FanProfile | null;
  profileError: string | null;
  buyingPower: number | null;
  ppvStats: { bought: number; total: number } | null;
  lists: FanList[];
  memberOf: Set<string>;
  note: string;
  loading: boolean;
  reload: () => void;
  setMembership: (listId: string, member: boolean) => Promise<void>;
  saveNote: (text: string) => Promise<void>;
  saveOfNote: (text: string) => Promise<void>;
}

function obj(v: unknown): Raw {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Raw) : {};
}
function money(...values: unknown[]): number | null {
  for (const v of values) {
    const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() ? Number(v) : NaN;
    if (Number.isFinite(n)) return n;
  }
  return null;
}
function text(...values: unknown[]): string {
  const hit = values.find((v) => typeof v === 'string' && v.trim());
  return hit ? String(hit) : '';
}

export function readProfile(body: unknown): { profile: FanProfile; listsStates: { id: string; name: string; member: boolean }[] } {
  const d = obj(dataOf(body));
  const user = obj(d.user ?? d.profile ?? d);
  const sub = obj(user.subscribedOnData);
  const by = obj(user.subscribedByData);
  const thumbs = obj(user.avatarThumbs);
  const states = [user.listsStates, user.lists_states, user.userLists, user.customLists, user.lists].find(Array.isArray) as Raw[] | undefined;
  return {
    profile: {
      // displayName is the name the creator gave the fan on OnlyFans (CRM chats.js resolveFanOfficialName).
      name: text(user.customName, user.custom_name, user.displayName, user.name, user.username),
      username: text(user.username),
      avatar: text(thumbs.c144, thumbs.c50, user.avatar) || null,
      verified: user.isVerified === true || user.is_verified === true,
      isOnline: user.isOnline === true || user.is_online === true,
      lastSeen: text(user.lastSeen, user.lastSeenAt, user.last_seen) || null,
      subscribedAt: text(sub.subscribeAt, sub.subscribe_at, sub.createdAt, sub.startDate, user.subscribedAt, user.subscribeAt) || null,
      duration: text(sub.duration, user.subscribedOnDuration, user.subscribedFor) || null,
      spent: {
        total: money(sub.totalSumm, by.totalSumm, user.totalSpent, user.total_spent),
        tips: money(sub.tipsSumm, user.tipsSumm),
        ppv: money(sub.messagesSumm, user.messagesSumm),
        subs: money(sub.subscribesSumm, user.subscribesSumm),
      },
      ofNote: text(d.notice, d.comment, user.notice, user.comment),
    },
    listsStates: (states || []).map((s) => ({
      id: String(s.id ?? s.list_id ?? ''),
      name: text(s.name, s.listName),
      member: s.hasUser === true || s.isAdded === true || s.inList === true || s.added === true || s.is_member === true || !!s.addedAt,
    })),
  };
}

export function useFan(accountId: string, fanId: string): FanData {
  const [profile, setProfile] = useState<FanProfile | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [buyingPower, setBuyingPower] = useState<number | null>(null);
  const [ppvStats, setPpvStats] = useState<{ bought: number; total: number } | null>(null);
  const [lists, setLists] = useState<FanList[]>([]);
  const [memberOf, setMemberOf] = useState<Set<string>>(new Set());
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setProfile(null);
    setProfileError(null);
    setBuyingPower(null);
    setPpvStats(null);
    setMemberOf(new Set());
    setNote('');
    const req = { accountId };
    const profileP = crmFetch('/v1/users/profile', { ...req, body: { fan_id: fanId } })
      .then((body) => {
        if (!alive) return;
        const { profile: p, listsStates } = readProfile(body);
        setProfile(p);
        if (listsStates.length) setMemberOf(new Set(listsStates.filter((s) => s.member).map((s) => s.id)));
        else
          crmFetch('/v1/lists/get-for-user', { ...req, body: { fan_id: fanId, limit: 200, offset: 0 } })
            .then((b) => alive && setMemberOf(new Set(listOf(dataOf(b)).map((l) => String(l.id ?? l.list_id)))))
            .catch(() => undefined);
      })
      .catch((err) => alive && setProfileError((err as Error).message));
    crmFetch('/v1/lists/get', { ...req, body: { limit: 200, offset: 0 } })
      .then((b) => alive && setLists(listOf(dataOf(b)).map((l) => ({ id: String(l.id), name: text(l.name, l.listName) || String(l.id) }))))
      .catch(() => undefined);
    crmFetch('/v1/buying-power/batch', { ...req, local: true, body: { fan_ids: [fanId] } })
      .then((b) => {
        const item = listOf(obj(dataOf(b)).items ?? dataOf(b))[0];
        const n = Number(item?.buying_power);
        if (alive) setBuyingPower(item?.buying_power != null && Number.isFinite(n) ? n : null);
      })
      .catch(() => undefined);
    crmFetch('/v1/chats/fan-ppv-stats', { ...req, local: true, body: { fan_id: fanId } })
      .then((b) => {
        const d = obj(dataOf(b));
        if (alive) setPpvStats({ bought: Number(d.bought) || 0, total: Number(d.total) || 0 });
      })
      .catch(() => undefined);
    crmFetch('/v1/chats/fan-notes/get', { ...req, local: true, body: { fan_id: fanId } })
      .then((b) => alive && setNote(String(obj(dataOf(b)).notes ?? '')))
      .catch(() => undefined);
    profileP.finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [accountId, fanId, tick]);

  const setMembership = useCallback(
    async (listId: string, member: boolean) => {
      await crmFetch(member ? '/v1/lists/add-fan' : '/v1/lists/remove-fan', { accountId, body: { fan_id: fanId, list_id: listId } });
      setMemberOf((prev) => {
        const next = new Set(prev);
        if (member) next.add(listId);
        else next.delete(listId);
        return next;
      });
    },
    [accountId, fanId],
  );

  const saveNote = useCallback(
    async (value: string) => {
      await crmFetch('/v1/chats/fan-notes/update', { accountId, local: true, body: { fan_id: fanId, notes: value.slice(0, 5000) } });
      setNote(value);
    },
    [accountId, fanId],
  );

  const saveOfNote = useCallback(
    async (value: string) => {
      await crmFetch('/v1/users/update-comment', { accountId, body: { fan_id: fanId, comment: value.trim() ? value : null } });
      setProfile((p) => (p ? { ...p, ofNote: value } : p));
    },
    [accountId, fanId],
  );

  return {
    profile,
    profileError,
    buyingPower,
    ppvStats,
    lists,
    memberOf,
    note,
    loading,
    reload: () => setTick((t) => t + 1),
    setMembership,
    saveNote,
    saveOfNote,
  };
}
